import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithPopup, signOut, onAuthStateChanged,
  signInWithEmailAndPassword, deleteUser, reauthenticateWithPopup,
} from 'firebase/auth';
import { collection, getDocs, writeBatch } from 'firebase/firestore';
import { auth, provider, db } from './firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, []);

  const signIn = () => signInWithPopup(auth, provider);
  const signInEmail = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const signOutUser = () => signOut(auth);

  const deleteAccount = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    // Delete all Firestore data first while auth is still valid
    const uid = currentUser.uid;
    const batch = writeBatch(db);
    const [ratingsSnap, metaSnap] = await Promise.all([
      getDocs(collection(db, 'users', uid, 'ratings')),
      getDocs(collection(db, 'users', uid, 'meta')),
    ]);
    ratingsSnap.forEach(d => batch.delete(d.ref));
    metaSnap.forEach(d => batch.delete(d.ref));
    await batch.commit();

    // Delete auth user — may require recent login
    try {
      await deleteUser(currentUser);
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        await reauthenticateWithPopup(currentUser, provider);
        await deleteUser(currentUser);
      } else {
        throw err;
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, signIn, signInEmail, signOut: signOutUser, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
